// One booking (Batch 4 redesign): the progress strip, a "Next" box with the
// one main step and why, then Money and Menu cards and the history. Edit,
// Invoice PDF and Cancel sit in the header / More menu. The step and its
// words come from cateringLogic.nextStep / bookingSteps.
import { useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ChefHat, ChevronDown, FileText } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import { formatMoney } from '@/lib/currency'
import { cn, formatDateHuman, formatDateTimeHuman } from '@/lib/utils'
import { T } from '@/lib/inventoryFlow/strings'
import { PT } from '@/lib/inventoryFlow/planStrings'
import { CBT, CT, CXT, PGT } from '@/lib/inventoryFlow/cateringStrings'
import { WU } from '@/lib/inventoryFlow/wrapUpStrings'
import { intentScope, useIntentKeys } from '@/lib/inventoryFlow/intentKey'
import type { BookingAction, BookingStatus, CateringBooking, PaymentKind, PaymentMethod } from '@/lib/inventoryFlow/cateringTypes'
import {
  actionNeedsConfirm, bookingActions, bookingEditable, bookingSteps, canClose, canTakeNext, canWriteBookings, defaultPaymentKind, firstDeposit, hasLivePlan,
  invoiceBusinessName, nextStep, parseAmount, paymentKindsFor, storeProgressOf, type NextStep,
} from '@/lib/inventoryFlow/cateringLogic'
import { ItemAvatar } from '@/components/inventory-flow/StatusChip'
import { ConfirmDialog } from './ConfirmDialog'
import { EquipmentClashNotice } from './EquipmentClashNotice'

/** A pre-filled amount, written the way staff read it (2,481,000); parseAmount takes the commas back out. */
const groupedAmount = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 2 })

type Notify = (kind: 'success' | 'error', message: string) => void

const money = (n: number) => formatMoney(n)

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message.split(' — ')[0] : 'Something went wrong'
}

export function localToday(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const STATUS_CLS: Record<BookingStatus, string> = {
  enquiry: 'bg-muted text-foreground',
  confirmed: 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-100',
  delivered: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  closed: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
  cancelled: 'bg-muted text-muted-foreground line-through',
}

export function BookingChip({ status }: { status: BookingStatus }) {
  return <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-semibold', STATUS_CLS[status])}>{CT.status[status]}</span>
}

/** Roles that may read a plan and its store request (production-plan read). */
const PLAN_READERS = ['admin', 'manager', 'inventory_manager', 'kitchen_manager']

function nextTitle(n: NextStep): string {
  switch (n.key) {
    case 'withKitchen': {
      const base = CXT.next.withKitchen.title
      return n.store ? `${base} — ${CXT.storeProgress(n.store.asked, n.store.sent)}` : base
    }
    case 'collect': return CXT.stillToPay(money(n.amount ?? 0))
    case 'refund': return CXT.toRefund(money(n.amount ?? 0))
    case 'cancelledRefund': return CXT.paidRefund(money(n.amount ?? 0))
  }
  return CXT.next[n.key].title
}

export function BookingView({ id, role, notify, onBack, onEdit }: {
  id: string; role: string; notify: Notify; onBack: () => void; onEdit: (b: CateringBooking) => void
}) {
  const qc = useQueryClient()
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({ queryKey: ['inventory-flow', 'catering', id], queryFn: () => apiClient.getCateringBooking(id) })
  const b = data?.data
  const writer = canWriteBookings(role)
  const businessName = invoiceBusinessName(useAllSettings())
  // The status step waiting for "are you sure?" (deliver, close, cancel).
  const [asking, setAsking] = useState<BookingAction | null>(null)
  const [reason, setReason] = useState('')
  const [downloading, setDownloading] = useState(false)
  const payRef = useRef<HTMLInputElement>(null)
  const refresh = () => qc.invalidateQueries({ queryKey: ['inventory-flow'] })

  // What the store has sent for the plan's request: "store asked for 9
  // items, 5 sent so far". Only for a live plan before delivery.
  const watchStore = Boolean(b && b.status === 'confirmed' && hasLivePlan(b) && PLAN_READERS.includes(role))
  const { data: planRes } = useQuery({
    queryKey: ['inventory-flow', 'plans', b?.plan_id],
    queryFn: () => apiClient.getProductionPlan(b?.plan_id ?? ''),
    enabled: watchStore,
  })
  const reqId = planRes?.data?.requisition_id
  const { data: reqRes } = useQuery({
    queryKey: ['inventory-flow', 'requisitions', reqId],
    queryFn: () => apiClient.getRequisition(reqId ?? ''),
    enabled: watchStore && Boolean(reqId),
  })

  // One key per action on this version of the booking, reused on a retry.
  const keys = useIntentKeys()
  const scope = (action: string) => intentScope(action, id, b?.updated_at ?? '')
  const act = useMutation({
    mutationFn: ({ action, reason }: { action: BookingAction; reason?: string }) => apiClient.cateringBookingAction(id, action, reason, keys.keyFor(scope(action))),
    onSuccess: (_r, { action }) => { keys.renew(scope(action)); refresh(); setAsking(null); setReason(''); notify('success', CT.actionDone[action]) },
    onError: (e) => { refresh(); notify('error', errMessage(e)) },
  })
  const toKitchen = useMutation({
    mutationFn: () => apiClient.sendBookingToKitchen(id, keys.keyFor(scope('plan'))),
    onSuccess: (r) => { keys.renew(scope('plan')); refresh(); notify('success', CT.sentToKitchen(r.data?.plan_no ?? '')) },
    onError: (e) => { refresh(); notify('error', errMessage(e)) },
  })

  if (isLoading) return <p className="text-muted-foreground">{T.loading}</p>
  if (isError || !b) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" className="px-2" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" /> {CT.back}</Button>
        <p className="text-destructive">{error ? errMessage(error) : CT.loadFailed}</p>
        <Button variant="outline" disabled={isFetching} onClick={() => void refetch()}>{T.retryNow}</Button>
      </div>
    )
  }

  const today = localToday()
  const steps = bookingSteps(b)
  const next = nextStep(b, today, watchStore ? storeProgressOf(reqRes?.data?.lines) : null)
  const mayAct = next.action != null && canTakeNext(next.action, role)
  const statusActions = writer ? bookingActions(b) : []
  // Status steps other than the next one stay reachable from More.
  const moreActions = statusActions.filter((a) => a !== 'cancel' && !(next.action === a))
  const askTitle = asking === 'cancel' ? CT.cancelTitle : asking === 'close' ? CT.closeTitle : CT.deliverTitle
  const askBody = asking === 'close' ? CT.closeBody : asking === 'deliver'
    ? [b.event_date > today ? CT.deliverEarly(formatDateHuman(b.event_date)) : '', hasLivePlan(b) ? CT.deliverBody : CT.deliverBodyNoPlan].filter(Boolean).join(' ')
    : undefined
  const runStatus = (a: BookingAction) => (actionNeedsConfirm(a) ? setAsking(a) : act.mutate({ action: a }))
  const download = async () => {
    setDownloading(true)
    try {
      await apiClient.downloadCateringInvoice(b.id)
    } catch (e) {
      notify('error', errMessage(e))
    } finally {
      setDownloading(false)
    }
  }
  const takeNext = () => {
    switch (next.action) {
      case 'confirm': return act.mutate({ action: 'confirm' })
      case 'send': return toKitchen.mutate()
      case 'deliver': return runStatus('deliver')
      case 'close': return runStatus('close')
      case 'pay':
        payRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        payRef.current?.focus({ preventScroll: true })
    }
  }
  const nextLabel = next.action === 'send' ? CT.sendToKitchen
    : next.action === 'pay' ? (next.key === 'collect' ? CXT.takePayment : CXT.giveRefund)
      : next.action ? CT.actions[next.action] : ''
  const busy = act.isPending || toKitchen.isPending
  const receipt = canClose(b.totals) && b.totals.total > 0
  const canOpenPlan = role === 'admin' || role === 'kitchen_manager'
  const editable = writer && bookingEditable(b.status)
  const perGuest = b.pricing_mode === 'per_guest'
  const nextBody = next.key === 'resend' ? CBT.kitchenCancelled
    : next.key === 'eventDone' && !hasLivePlan(b) ? CT.deliverBodyNoPlan
      : CXT.next[next.key].body

  return (
    <div className="space-y-5">
      <Button variant="ghost" className="px-2" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" /> {CT.back}</Button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold">{b.event_name ? `${b.event_name} — ${b.customer_name}` : b.customer_name}</h2>
            <BookingChip status={b.status} />
          </div>
          <p className="text-sm text-muted-foreground">
            {[
              `${b.booking_no}${b.revision > 1 ? ` (${CT.revision(b.revision)})` : ''}`,
              `${formatDateHuman(b.event_date)}${b.event_time ? `, ${b.event_time}` : ''}`,
              b.venue, b.guest_count ? CT.guests(b.guest_count) : null, b.order_type === 'dine_in' ? CT.dineIn : CT.takeaway, b.customer_phone,
            ].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {editable && <Button variant="outline" onClick={() => onEdit(b)}>{CT.edit}</Button>}
          <Button variant="outline" disabled={downloading} onClick={() => void download()}>
            <FileText className="mr-2 h-4 w-4" /> {receipt ? CT.receipt : CT.invoice}
          </Button>
          {writer && (moreActions.length > 0 || statusActions.includes('cancel')) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">{CXT.more} <ChevronDown className="ml-2 h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {moreActions.map((a) => (
                  <DropdownMenuItem key={a} disabled={busy || (a === 'close' && !canClose(b.totals))} onSelect={() => runStatus(a)}>{CT.actions[a]}</DropdownMenuItem>
                ))}
                {statusActions.includes('cancel') && (
                  <DropdownMenuItem className="text-destructive focus:text-destructive" disabled={busy} onSelect={() => setAsking('cancel')}>{CT.actions.cancel}</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {!writer && <p className="text-sm text-muted-foreground">{CT.readOnly}</p>}
      {!businessName && (
        <p className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {CT.noBusinessName}{' '}
          {(role === 'admin' || role === 'manager') && <Link to="/admin/settings" search={{ section: 'general' }} className="font-semibold underline">{CT.setBusinessName}</Link>}
        </p>
      )}

      {steps && (
        <ol aria-label={CXT.progress} className={cn('grid gap-2 grid-cols-2 sm:grid-cols-3', steps.length === 6 ? 'lg:grid-cols-6' : 'lg:grid-cols-5')}>
          {steps.map(({ step, state }) => (
            <li key={step} aria-current={state === 'now' ? 'step' : undefined}
              className={cn('rounded-lg border px-3 py-2',
                state === 'done' && 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40',
                state === 'now' && 'border-primary bg-primary text-primary-foreground',
                state === 'todo' && 'text-muted-foreground')}>
              <div className={cn('text-xs font-semibold', state === 'done' && 'text-emerald-700 dark:text-emerald-300', state === 'now' && 'opacity-80')}>{CXT.stepState[state]}</div>
              <div className="text-sm font-medium">{CXT.steps[step]}</div>
            </li>
          ))}
        </ol>
      )}

      <div className="flex flex-wrap items-center gap-4 rounded-lg border bg-muted/30 p-4">
        <div className="min-w-0 flex-[1_1_18rem]">
          <div className="text-base font-semibold">{nextTitle(next)}</div>
          <p className="text-sm text-muted-foreground">{nextBody}{next.action && !mayAct ? ` ${CXT.noAction}` : ''}</p>
        </div>
        {next.action && mayAct && (
          <Button disabled={busy} onClick={takeNext}>{nextLabel}</Button>
        )}
        {next.key === 'withKitchen' && writer && (
          <Button variant="outline" disabled={busy} onClick={() => runStatus('deliver')}>{CXT.deliverEarlyLabel(formatDateHuman(b.event_date))}</Button>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <MoneyCard booking={b} writer={writer} notify={notify} onChanged={refresh} payRef={payRef}
          preferKind={next.key === 'refund' || next.key === 'cancelledRefund' ? 'refund' : undefined} />

        <section className="space-y-3 rounded-lg border bg-card p-4">
          <h3 className="text-base font-semibold">{CXT.menu} <span className="font-normal text-muted-foreground">· {CXT.menuSummary(PT.dishCount(b.dish_count), PT.portions(b.portion_count))}</span></h3>
          {perGuest && <p className="text-sm text-muted-foreground">{PGT.menuIncluded}</p>}
          <ul className="divide-y">
            {(b.lines ?? []).map((l) => (
              <li key={l.id} className="flex items-center gap-3 py-2">
                {l.image_url ? <img src={l.image_url} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <ItemAvatar name={l.name} />}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{l.name}</span>
                  {!perGuest && (
                    <span className="text-xs text-muted-foreground">
                      {money(l.unit_price)}{l.unit_price !== l.menu_price && <span className="ml-1 line-through">{money(l.menu_price)}</span>} · {money(l.amount)}
                    </span>
                  )}
                  {l.note && <span className="mt-0.5 block text-xs font-medium text-amber-800 dark:text-amber-200">{l.note}</span>}
                </span>
                <span className="text-sm font-semibold tabular-nums">{PT.portions(l.qty)}</span>
              </li>
            ))}
          </ul>
          {b.notes && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
              <div className="text-xs font-semibold">{CXT.customerNoteSent}</div>
              <p className="whitespace-pre-wrap">{b.notes}</p>
            </div>
          )}
          <EquipmentClashNotice clashes={b.equipment_clashes} />
          {(b.extras ?? []).length > 0 && (
            <p className="text-sm text-muted-foreground"><span className="font-medium text-foreground">{PT.extrasTitle}:</span> {(b.extras ?? []).map((x) => `${x.name} × ${x.qty} ${x.unit}`).join(', ')}</p>
          )}
          <div className="flex flex-wrap items-center gap-3 border-t pt-3">
            <ChefHat className="h-4 w-4 text-muted-foreground" />
            {b.plan_id && b.plan_no ? (
              <>
                <span className="text-sm font-medium">{CXT.kitchenLine(b.plan_no, b.plan_status ? PT.status[b.plan_status as keyof typeof PT.status] ?? b.plan_status : '')}</span>
                {b.plan_wrapped_up_at && <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">{WU.bookingWrapped}</span>}
                {canOpenPlan && (
                  <Button asChild variant="outline" size="sm" className="ml-auto">
                    <Link to="/admin/production" search={{ plan: b.plan_id }}>{CT.openPlan}</Link>
                  </Button>
                )}
              </>
            ) : (
              <span className="text-sm text-muted-foreground">{b.status === 'confirmed' ? CT.notSentYet : b.status === 'enquiry' ? CT.confirmFirst : CT.kitchen}</span>
            )}
          </div>
        </section>
      </div>

      <section className="rounded-lg border bg-card p-4">
        <h3 className="mb-2 text-base font-semibold">{CT.history}</h3>
        <ol className="space-y-1.5">
          {(b.events ?? []).map((e) => (
            <li key={e.id} className="flex flex-wrap gap-x-3 text-sm">
              <span className="text-muted-foreground tabular-nums">{formatDateTimeHuman(e.created_at)}</span>
              <span className="font-medium">{CT.events[e.event_type] ?? e.event_type}</span>
              {e.revision > 1 && <span className="text-muted-foreground">{CT.revision(e.revision)}</span>}
              {e.actor_name && <span className="text-muted-foreground">· {e.actor_name}</span>}
              {e.note && <span>· {e.note}</span>}
            </li>
          ))}
        </ol>
      </section>

      <ConfirmDialog open={asking !== null} title={askTitle} body={askBody}
        confirmLabel={asking === 'cancel' ? CT.cancelConfirm : asking ? CT.actions[asking] : ''} cancelLabel={asking === 'cancel' ? CT.keep : CT.notYet}
        destructive={asking === 'cancel'} busy={act.isPending}
        onConfirm={() => asking && act.mutate(asking === 'cancel' ? { action: 'cancel', reason } : { action: asking })}
        onCancel={() => { setAsking(null); setReason('') }}>
        {asking === 'cancel' && <Input placeholder={CT.cancelReason} aria-label={CT.cancelReason} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </ConfirmDialog>
      <p className="text-xs text-muted-foreground">{CT.catering}</p>
    </div>
  )
}

/** Total / Paid / Still to pay, the payments, and the payment form. */
function MoneyCard({ booking: b, writer, notify, onChanged, payRef, preferKind }: {
  booking: CateringBooking; writer: boolean; notify: Notify; onChanged: () => void; payRef: React.RefObject<HTMLInputElement>; preferKind?: PaymentKind
}) {
  const kinds = writer ? paymentKindsFor(b.status, b.totals.paid) : []
  // null = not picked: Deposit until a deposit is on the booking, then Payment
  // (a refund when the next step is giving money back).
  const [kind, setKind] = useState<PaymentKind | null>(null)
  const [method, setMethod] = useState<PaymentMethod>('cash')
  const [amount, setAmount] = useState('')
  const [receivedOn, setReceivedOn] = useState(localToday)
  const [reference, setReference] = useState('')
  const [voiding, setVoiding] = useState<string | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const activeKind = kind && kinds.includes(kind) ? kind : preferKind && kinds.includes(preferKind) ? preferKind : defaultPaymentKind(kinds, b.payments)
  useEffect(() => {
    // Suggest the balance for a payment, what was paid for a refund.
    if (activeKind === 'payment' && b.totals.balance > 0) setAmount(groupedAmount(b.totals.balance))
    // Overpaid: just the extra; otherwise (or cancelled) everything paid.
    else if (activeKind === 'refund') setAmount(groupedAmount(b.status !== 'cancelled' && b.totals.balance < -0.005 ? -b.totals.balance : Math.max(b.totals.paid, 0)))
    else setAmount('')
  }, [activeKind, b.totals.balance, b.totals.paid, b.status])

  // One key per payment as typed: tapping Record again after a lost answer
  // replays the first instead of taking the money twice.
  const today = localToday()
  const amt = parseAmount(amount)
  const futureDate = receivedOn > today
  // Today's payments carry no date, so the server stamps the real time.
  const payment = {
    kind: activeKind!, method, amount: amt ?? 0, reference: reference.trim() || undefined,
    ...(receivedOn && receivedOn !== today ? { received_on: receivedOn } : {}),
  }
  const keys = useIntentKeys()
  const addScope = intentScope('payment', b.id, payment)
  const voidScope = (pid: string) => intentScope('void-payment', b.id, pid)
  const add = useMutation({
    mutationFn: () => apiClient.addBookingPayment(b.id, payment, keys.keyFor(addScope)),
    onSuccess: () => { keys.renew(addScope); onChanged(); setReference(''); setKind(null); setReceivedOn(localToday()); notify('success', CT.recorded) },
    onError: (e) => { onChanged(); notify('error', errMessage(e)) },
  })
  const voidIt = useMutation({
    mutationFn: (pid: string) => apiClient.voidBookingPayment(b.id, pid, voidReason.trim(), keys.keyFor(voidScope(pid))),
    onSuccess: (_r, pid) => { keys.renew(voidScope(pid)); onChanged(); setVoiding(null); setVoidReason(''); notify('success', CT.voidedOk) },
    onError: (e) => { onChanged(); notify('error', errMessage(e)) },
  })
  const tooMuchRefund = activeKind === 'refund' && amt != null && amt > b.totals.paid + 0.005
  const badAmount = amount.trim() !== '' && amt == null
  const t = b.totals
  const live = (b.payments ?? []).filter((p) => !p.voided_at && p.kind !== 'refund')
  const dep = firstDeposit(b.payments)
  // "Paid (deposit 20 Oct, bank)" when the deposit is all that was paid.
  const paidLabel = dep && live.length === 1 && t.paid > 0 ? CXT.paidDeposit(formatDateHuman(dep.received_at), CT.methods[dep.method]) : CXT.paidLine
  const row = (label: string, v: string, cls?: string) => (
    <div className={cn('flex justify-between gap-3 text-sm tabular-nums', cls)}><span>{label}</span><span>{v}</span></div>
  )

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <h3 className="text-base font-semibold">{CXT.money}</h3>
      <div className="space-y-1.5">
        {b.pricing_mode === 'per_guest' && b.guest_count != null && b.price_per_guest != null
          ? row(PGT.guestsTimes(b.guest_count, money(b.price_per_guest)), money(t.subtotal), 'text-muted-foreground')
          : t.discount > 0 && row(CT.subtotal, money(t.subtotal), 'text-muted-foreground')}
        {t.discount > 0 && row(`${CT.discount}${b.discount_note ? ` (${b.discount_note})` : ''}`, `−${money(t.discount)}`, 'text-muted-foreground')}
        {row(CT.total, money(t.total), 'font-semibold')}
        {row(paidLabel, money(t.paid), 'text-muted-foreground')}
        {b.status !== 'cancelled' && (t.balance < -0.005
          ? row(CXT.owedBackLabel, money(-t.balance), 'border-t pt-2 font-bold text-amber-700 dark:text-amber-300')
          : row(CXT.stillToPayLabel, money(Math.max(t.balance, 0)), cn('border-t pt-2 font-bold', t.balance > 0.005 && 'text-red-700 dark:text-red-400')))}
      </div>

      {(b.payments ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">{CT.noPayments}</p>
      ) : (
        <ul className="divide-y border-t">
          {(b.payments ?? []).map((p) => (
            <li key={p.id} className="space-y-2 py-2">
              <div className={cn('flex flex-wrap items-center gap-2 text-sm', p.voided_at && 'text-muted-foreground line-through')}>
                <span className="min-w-[6rem] tabular-nums">{formatDateHuman(p.received_at)}</span>
                <span className="font-medium">{CT.kinds[p.kind]}</span>
                <span>{CT.methods[p.method]}{p.reference ? ` · ${p.reference}` : ''}</span>
                <span className="ml-auto font-semibold tabular-nums">{p.kind === 'refund' ? '−' : ''}{money(p.amount)}</span>
                {writer && !p.voided_at && b.status !== 'closed' && (
                  <Button variant="ghost" size="sm" onClick={() => setVoiding(p.id)}>{CT.voidIt}</Button>
                )}
              </div>
              {p.voided_at && <p className="text-xs text-muted-foreground">{CT.voided}: {p.void_reason}</p>}
              {voiding === p.id && (
                <div className="flex flex-wrap gap-2">
                  <Input className="min-w-[12rem] flex-1" placeholder={CT.voidReason} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
                  <Button variant="destructive" disabled={!voidReason.trim() || voidIt.isPending} onClick={() => voidIt.mutate(p.id)}>{CT.voidConfirm}</Button>
                  <Button variant="ghost" onClick={() => setVoiding(null)}>{CT.keep}</Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {activeKind && (
        <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
          <h4 className="text-sm font-semibold">{CT.addPayment}</h4>
          <div className="flex flex-wrap gap-2">
            <div className="flex w-fit flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="radiogroup" aria-label={CT.kind}>
              {kinds.map((k) => (
                <button key={k} type="button" role="radio" aria-checked={activeKind === k} onClick={() => setKind(k)}
                  className={cn('rounded-md px-3 py-1.5 text-sm font-medium', activeKind === k ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                  {CT.kinds[k]}
                </button>
              ))}
            </div>
            <div className="flex w-fit flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="radiogroup" aria-label={CT.method}>
              {(['cash', 'bank_transfer', 'card'] as const).map((m) => (
                <button key={m} type="button" role="radio" aria-checked={method === m} onClick={() => setMethod(m)}
                  className={cn('rounded-md px-3 py-1.5 text-sm font-medium', method === m ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                  {CT.methods[m]}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">{CT.amount}</span>
              <Input ref={payRef} inputMode="decimal" aria-invalid={badAmount || undefined} className="w-36" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">{CT.receivedOn}</span>
              <Input type="date" max={today} className="w-44" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
            </label>
            <Input className="min-w-[10rem] flex-1" maxLength={100} placeholder={CT.referenceHint} aria-label={CT.reference} value={reference} onChange={(e) => setReference(e.target.value)} />
            <Button disabled={amt == null || tooMuchRefund || futureDate || add.isPending} onClick={() => add.mutate()}>{CT.record}</Button>
          </div>
          {badAmount && <p className="text-sm text-destructive">{CT.amountInvalid}</p>}
          {futureDate && <p className="text-sm text-destructive">{CT.receivedFuture}</p>}
          {tooMuchRefund && <p className="text-sm text-destructive">{CT.refundMax(money(b.totals.paid))}</p>}
        </div>
      )}
    </section>
  )
}
